// Vendor Program v2 PR 2 — vendor record Reports tab: outstanding
// (expected-vs-filed) chips up top, then the monthly report list. Row click
// opens the shared review drawer, deep-linkable via ?tab=reports&report=<id>.

import { formatPeriodMonth } from "@/design-system/lib/format";
import { useSearchParams } from "react-router-dom";
import {
  Card,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import {
  useVendorReportCompliance,
  useVendorReports,
} from "@/hooks/portfolioAdmin/useVendorReports";
import type { VendorDto } from "@/api/portfolioAdmin/types";
import type { VendorMonthlyReportDto } from "@/api/portfolioAdmin/vendorReportsApi";
import { fmtMoney } from "../billing/vendorBillingFormat";
import { VendorReportReviewDrawer } from "../reports/VendorReportReviewDrawer";
import {
  VENDOR_REPORT_STATUS_LABEL,
  vendorReportComplianceBadge,
  vendorReportStatusTone,
} from "../reports/vendorReportFormat";

interface VendorReportsTabProps {
  vendor: VendorDto;
}

/** F11 — regulated (borrower-paid) reports have no invoice amount; their
 *  self-reported figure is the collected-from-borrower TOTAL. */
function selfReportedCell(report: VendorMonthlyReportDto): string {
  if (report.isRegulatedVendor) {
    const lines = report.collectedLineItems ?? [];
    if (lines.length > 0) return fmtMoney(lines.reduce((sum, l) => sum + l.amount, 0));
    return report.collectedNothingConfirmed ? "None collected" : "—";
  }
  return report.selfReportedInvoiceAmount != null
    ? fmtMoney(report.selfReportedInvoiceAmount)
    : "—";
}

export function VendorReportsTab({ vendor }: VendorReportsTabProps) {
  const { date } = useDateFormatter();
  const [params, setParams] = useSearchParams();
  const openReportId = params.get("report");

  const reportsQuery = useVendorReports({ vendorId: vendor.id });
  const complianceQuery = useVendorReportCompliance({ vendorId: vendor.id });

  // Period boundaries are wall-calendar months stored at UTC midnight —
  // rendering them through the tz-aware formatter shifted June into May.
  const month = (iso: string) => formatPeriodMonth(iso, "short");

  const setReportParam = (value: string | null) => {
    const updated = new URLSearchParams(params);
    if (value) updated.set("report", value);
    else updated.delete("report");
    setParams(updated, { replace: true });
  };

  if (reportsQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading reports…</p>
      </Card>
    );
  }
  if (reportsQuery.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load this vendor&apos;s reports. Refresh to retry.
        </p>
      </Card>
    );
  }

  const rows = reportsQuery.data ?? [];
  const compliance = complianceQuery.data;
  const outstandingCount = compliance?.outstanding.length ?? 0;
  const badge = compliance ? vendorReportComplianceBadge(compliance) : null;

  return (
    <div className="flex flex-col gap-space-4" data-testid="vendor-reports-tab">
      <Card>
        <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
          <h3 className="font-display font-bold text-h3 m-0">Monthly reports</h3>
          <div className="flex items-center gap-space-2">
            {badge && (
              <StatusBadge
                tone={badge.tone}
                hint={badge.hint}
                data-testid="vendor-reports-outstanding-chip"
              >
                {badge.label}
              </StatusBadge>
            )}
          </div>
        </div>

        {outstandingCount > 0 && (
          <ul
            className="m-0 mb-space-3 p-0 list-none flex flex-col gap-space-1"
            data-testid="vendor-reports-outstanding-list"
          >
            {compliance!.outstanding.map((o) => (
              <li
                key={`${o.assignmentId}-${o.periodStart}`}
                className="flex items-center justify-between gap-space-2 text-small border-b border-border-light pb-space-1"
              >
                <span>
                  {month(o.periodStart)} · {o.loanNumber} · {o.roleOnLoan}
                </span>
                <StatusBadge tone={o.status === "missing" ? "danger" : "warn"}>
                  {o.status === "missing"
                    ? "Missing"
                    : o.status === "returned"
                      ? "Returned"
                      : "Draft"}
                </StatusBadge>
              </li>
            ))}
          </ul>
        )}

        {rows.length === 0 ? (
          <p className="text-small text-gray-mid m-0">
            No monthly reports from this vendor yet. Reports are filed by the
            vendor from their portal each month.
          </p>
        ) : (
          <Table data-testid="vendor-reports-table">
            <THead>
              <TR>
                <TH className="text-white-pure">Period</TH>
                <TH className="text-white-pure">Loan</TH>
                <TH className="text-white-pure">Role</TH>
                <TH className="text-white-pure">Status</TH>
                <TH className="text-white-pure text-right">Self-reported amount</TH>
                <TH className="text-white-pure">Submitted</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((report) => (
                <TR
                  key={report.id}
                  onClick={() => setReportParam(report.id)}
                  className="cursor-pointer"
                  data-testid={`vendor-report-row-${report.id}`}
                >
                  <TD className="font-medium">{month(report.periodStart)}</TD>
                  <TD>{report.loanNumber}</TD>
                  <TD>{report.roleOnLoan}</TD>
                  <TD>
                    <span className="inline-flex items-center gap-space-1">
                      <StatusBadge tone={vendorReportStatusTone(report.status)}>
                        {VENDOR_REPORT_STATUS_LABEL[report.status]}
                      </StatusBadge>
                      {report.revisionNumber > 1 && (
                        <StatusBadge
                          tone="neutral"
                          hint={`Revision ${report.revisionNumber} — resubmitted after a return.`}
                        >
                          Resubmitted
                        </StatusBadge>
                      )}
                    </span>
                  </TD>
                  <TD className="text-right tabular-nums">{selfReportedCell(report)}</TD>
                  <TD>{report.submittedAt ? date(report.submittedAt) : "—"}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {openReportId && (
        <VendorReportReviewDrawer
          reportId={openReportId}
          open={!!openReportId}
          onClose={() => setReportParam(null)}
        />
      )}
    </div>
  );
}
